import { i18next, view, clipboard, blocks, dialogs, hdrm } from '@yola/ws-sdk';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import contextMenu from 'src/js/modules/context-menu';
import canPasteBlock from '../verifiers/can-paste-block';
import getRootBlockByElement from '../helpers/get-root-block-by-element';
import copyBlockToClipboard from '../helpers/copy-block-to-clipboard';
import getBlockFromClipboard from '../helpers/get-block-from-clipboard';
import getBlockDataFromClipboard from '../helpers/get-block-data-from-clipboard';
import { CONTEXT_MENU_GROUP_NAME } from '../constants/common';
import {
  COPY_BLOCK,
  CUT_BLOCK,
  PASTE_BEFORE_THIS_BLOCK,
  PASTE_AFTER_THIS_BLOCK,
} from '../constants/context-menu-block-items';

const { updateStrategies } = hdrm.constants;

const showPasteWarning = (targetElementId, position, customColorsData) => {
  const clipboardData = clipboard.accessors.getClipboardData();
  const { element } = clipboardData;
  const elementBlockId = blocks.accessors.getBlockIdByElement(element);
  const redundantElements = blocks.helpers.getElementsToBeReplaced(elementBlockId);

  // We try to cover case when there is more then one redundant block
  if (redundantElements.length > 1) {
    dialogs.operations.show(dialogTypes.BLOCK_LIMIT_DIALOG, {
      onSubmit: () => {
        dialogs.operations.hide();
      },
      blockId: elementBlockId,
    });
    return;
  }

  const [oldBlockElement] = redundantElements;
  const elementIdToDelete = view.accessors.getLiveElementId(oldBlockElement);
  const oldBlockId = blocks.accessors.getBlockIdByElement(oldBlockElement);
  const oldVariationId = blocks.accessors.getVariationIdByElement(oldBlockElement);
  const newVariationId = blocks.accessors.getVariationIdByElement(element);

  const deleteOperation = [view.operations.deleteElement, [elementIdToDelete]];
  const pasteOperation = [
    view.operations.pasteElement,
    [targetElementId, position, { customColorsData, isBlockReplaced: true }],
  ];

  dialogs.operations.show(dialogTypes.BLOCK_REPLACEMENT_DIALOG, {
    onSubmit: () => {
      view.operations.bulkViewOperations([deleteOperation, pasteOperation]);
      dialogs.operations.hide();
    },
    onCancel: () => {
      dialogs.operations.hide();
    },
    oldBlock: oldBlockId,
    oldBlockVariationId: oldVariationId,
    newBlock: elementBlockId,
    newBlockVariationId: newVariationId,
  });
};

const pasteBlock = (element, position, customColorsData) => {
  const elementId = view.accessors.getLiveElementId(element);
  if (canPasteBlock(element.parentNode)) {
    view.operations.pasteElement(elementId, position, {
      customColorsData,
    });
    return;
  }
  showPasteWarning(elementId, position, customColorsData);
};

const isPasteDisabled = async () => {
  const data = await getBlockDataFromClipboard();

  if (data) {
    return false;
  }

  return !clipboard.verifiers.hasClipboardElementData();
};

export const registerContextMenuBlockItems = () => {
  const itemsGroup = {
    groupName: CONTEXT_MENU_GROUP_NAME,
    matches: (element) => Boolean(getRootBlockByElement(element)),
    prepareElement: (element) => getRootBlockByElement(element),
    items: [
      {
        id: COPY_BLOCK,
        caption: i18next.t('Copy block'),
        async onClick(element) {
          contextMenu.operations.hideContextMenu();
          const elementId = view.accessors.getLiveElementId(getRootBlockByElement(element));
          await copyBlockToClipboard(elementId);
          view.operations.copyElement(elementId);
        },
      },
      {
        id: CUT_BLOCK,
        caption: i18next.t('Cut block'),
        async onClick(element) {
          contextMenu.operations.hideContextMenu();
          const elementId = view.accessors.getLiveElementId(getRootBlockByElement(element));
          const options = { strategy: updateStrategies.UPDATE_STATIC_ONLY };
          await copyBlockToClipboard(elementId);
          view.operations.cutElement(elementId, options);
        },
      },
      {
        id: PASTE_BEFORE_THIS_BLOCK,
        caption: i18next.t('Paste before this block'),
        disabled: isPasteDisabled,
        async onClick(element) {
          contextMenu.operations.hideContextMenu();
          const blockElement = await getBlockFromClipboard();
          const data = await getBlockDataFromClipboard();

          if (blockElement) {
            clipboard.operations.setClipboardData(blockElement);
          }

          pasteBlock(getRootBlockByElement(element), 'beforebegin', data?.parsedData?.customColors);
        },
      },
      {
        id: PASTE_AFTER_THIS_BLOCK,
        caption: i18next.t('Paste after this block'),
        disabled: isPasteDisabled,
        async onClick(element) {
          contextMenu.operations.hideContextMenu();
          const blockElement = await getBlockFromClipboard();
          const data = await getBlockDataFromClipboard();

          if (blockElement) {
            clipboard.operations.setClipboardData(blockElement);
          }

          pasteBlock(getRootBlockByElement(element), 'afterend', data?.parsedData?.customColors);
        },
      },
    ],
  };

  contextMenu.operations.registerItemsGroup(itemsGroup);
};

export { registerBlockVisibilityContextMenu } from './register-block-visibility-context-menu';
export { registerWidgetVisibilityContextMenu } from './register-widget-visibility-context-menu';
